/**
 * The paid add-ons, per customer.
 *
 * Each is a switch a sysadmin flips; the product then shows or hides the
 * module for everyone at that organization (the sidebar item, and the
 * callables behind it, both check the same flag). Off is the default for a
 * new customer, so the state is stated plainly with when it was turned on.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import { ADDON_KEYS, type AddonKey } from '../org/useAddons';
import { formatDay } from '../../shared/dates';
import own from './Console.module.css';
import page from '../../shared/Page.module.css';
import { GroupCard } from '../../shared/GroupCard';

export const ADDON_LABEL: Record<AddonKey, string> = {
  scopeOfService: 'Scope of Service',
  exceptionManagement: 'Exceptions & Variances',
  competencyEvidence: 'Competency & Training',
  formsAttestation: 'Forms & Attestations',
  lippincottIntegration: 'Lippincott integration',
};

export const ADDON_BLURB: Record<AddonKey, string> = {
  scopeOfService: 'What each unit is authorized to do, approved and versioned for surveyors.',
  exceptionManagement: 'Approved, time-limited departures from a policy, with risk-based sign-off.',
  competencyEvidence: 'Skills staff must prove, assigned with due dates, with evidence and approval.',
  formsAttestation: 'Declarations and agreements staff sign, kept exactly as answered.',
  lippincottIntegration: 'Procedure content from Lippincott, linked to policies.',
};

/** The four modules first, integrations after. */
export const ADDON_ORDER: readonly AddonKey[] = [
  'scopeOfService', 'exceptionManagement', 'competencyEvidence', 'formsAttestation', 'lippincottIntegration',
];

export interface AddonState {
  enabled: boolean;
  enabledAt?: string;
  seededAt?: string;
  seedCount?: number;
}

export function AddonsSection({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const addons = useQuery({
    queryKey: ['console-org-addons', orgId],
    retry: false,
    queryFn: () => call<{ orgId: string }, Record<string, AddonState>>('getOrgAddons', { orgId }),
  });
  const setAddon = useMutation({
    mutationFn: (input: { addonName: AddonKey; enabled: boolean }) =>
      call<{ orgId: string; addonName: AddonKey; enabled: boolean }, unknown>('sysAdminSetAddon', { orgId, ...input }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['console-org-addons', orgId] }),
  });

  const data = addons.data ?? {};
  const busy = setAddon.isPending ? setAddon.variables?.addonName : null;

  return (
    <GroupCard id="addons-title" title="Add-ons">
      <p style={{ color: 'var(--ink-2)', lineHeight: 1.6, maxWidth: '68ch', margin: '0 0 var(--sp-4)' }}>
        Off for a new customer. Turning one on shows the module to everyone at the organization
        at once, and the first time adds a starter pack of sample records they can edit or delete;
        turning it off hides it again and keeps their records.
      </p>

      {(addons.error || setAddon.error) && (
        <div className={own.error} role="alert">
          {isCallFailure(addons.error ?? setAddon.error) ? (addons.error ?? setAddon.error as { message: string }).message : 'Could not read or change the add-ons.'}
        </div>
      )}

      {addons.isLoading ? (
        <p className={page.itemMeta}>Loading…</p>
      ) : (
        <ul className={page.list}>
          {ADDON_ORDER.filter((k) => (ADDON_KEYS as readonly string[]).includes(k)).map((key) => {
            const state = data[key];
            const on = state?.enabled === true;
            return (
              <li key={key} className={page.listItem}>
                <div>
                  <div className={page.itemName}>
                    {ADDON_LABEL[key]}{' '}
                    <span className={`${page.tag} ${on ? page.ok : page.neutral}`} style={{ marginLeft: 8 }}>{on ? 'On' : 'Off'}</span>
                  </div>
                  <div className={page.itemMeta}>
                    {ADDON_BLURB[key]}
                    {on && state?.enabledAt && ` Turned on ${formatDay(state.enabledAt)}.`}
                    {on && state?.seedCount ? ` ${state.seedCount} sample records were added when it was turned on.` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className={own.googleBtn}
                  style={{ width: 'auto', padding: '9px 16px' }}
                  disabled={setAddon.isPending}
                  aria-label={`${on ? 'Turn off' : 'Turn on'} ${ADDON_LABEL[key]}`}
                  onClick={() => setAddon.mutate({ addonName: key, enabled: !on })}
                >
                  {busy === key ? 'Saving…' : on ? 'Turn off' : 'Turn on'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </GroupCard>
  );
}
